import { useEffect, useMemo, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Trans, useTranslation } from 'react-i18next'
import request, { getAxiosRequestConfig } from 'utils/request.ts'
import { Methods } from 'types/request.ts'
import styles from './FeedbackWidget.module.scss'
import { FeedbackWidgetInputs, FeedbackWidgetProps } from './types.ts'
import { Button, Card, Form, Heading, Icon, Rating, TextArea } from '@boltenergy-be/design-system'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { updateContact } from 'store/contact/thunks'
import classNames from 'classnames'
import { IS_BETA } from 'constants/envs.ts'
import { trackEvent } from 'utils/tracking.ts'
import { FeedbackWidgetStep } from './constants.ts'
import { dismissFeedbackWidgets } from './utils.ts'
import parse from 'html-react-parser'
import { Controller, useForm, useWatch } from 'react-hook-form'
import Link from 'components/link/Link.tsx'
import { BOLT_EMAIL_CONTACT, BOLT_PHONE_CONTACT } from 'constants/constants.ts'

const FeedbackWidget = ({ id, openFeedbackWidget, showTrigger, data, isGeneralFeedback, className }: FeedbackWidgetProps) => {
  // Redux
  const { contact } = useStoreSelector((store) => store.contact)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['feedbackWidget', 'common', 'validation'])
  const language = useGetLanguage()

  // Local state
  const [widgetOpen, setWidgetOpen] = useState<boolean>(false)
  const [step, setStep] = useState<FeedbackWidgetStep>('rating')
  const [widgetFinalized, setWidgetFinalized] = useState<boolean>(false)
  const [localHideTrigger, setLocalHideTrigger] = useState<boolean>(false)

  // React Hook Form
  const hookForm = useForm<FeedbackWidgetInputs>({})
  const { control, register, reset, handleSubmit } = hookForm
  const watchRating = useWatch({ control, name: 'rating' })

  /**
   * Opens the widget from parent
   */
  useEffect(() => {
    setWidgetOpen(!!openFeedbackWidget)
  }, [openFeedbackWidget])

  // Constants
  const ratingTitle = t(($) => $.rating.title, {
    context: isGeneralFeedback ? 'general' : 'specific'
  })
  const feedbackTitle = t(($) => $.feedback.title[watchRating > 3 ? 'happy' : 'sad'])
  const testingTitle = contact?.userTester ? t(($) => $.userTesting.title.reConfirmation) : t(($) => $.userTesting.title.new)
  const currentTitle = useMemo<string>(() => {
    switch (step) {
      case 'rating':
        return ratingTitle
      case 'feedback':
        return feedbackTitle
      case 'userTesting':
        return testingTitle
    }
  }, [feedbackTitle, ratingTitle, step, testingTitle])

  /**
   * Handles the on submit
   */
  const submit = async (inputs: FeedbackWidgetInputs): Promise<void> => {
    // Push entry to API
    try {
      await request(
        getAxiosRequestConfig(Methods.POST, '/feedback-widgets/entry', {
          feedbackWidgetId: id,
          score: inputs.rating,
          feedback: inputs.feedback || '',
          data,
          platform: Capacitor.getPlatform()
        })
      )

      if (IS_BETA) {
        setWidgetFinalized(true)
      } else {
        setStep('userTesting')
      }
    } catch (err) {
      const { message } = err as Error
      throw new Error(message)
    }
  }

  /**
   * Close the widget, a widget that opened on its own (no trigger) also hides all other feedback widgets for 30 days
   */
  const closeWidget = () => {
    if (!showTrigger) {
      dismissFeedbackWidgets()
    }

    // close the widget
    setWidgetOpen(false)

    if (step === 'userTesting') {
      setLocalHideTrigger(true)
    }
  }

  /**
   * Handles the click on the testing button
   * @param {boolean} value
   */
  const handleClickTestingButton = (value: boolean) => {
    if (contact?.sfId) {
      void dispatch(updateContact({ contactId: contact.sfId, contact: { userTester: value } }))
    }

    setWidgetFinalized(true)
  }

  /**
   * Finish the widget with toast, closing & reset
   */
  const closeFinalizedWidget = () => {
    setLocalHideTrigger(true)
    closeWidget()

    // Rest the widget after the CSS transition
    setTimeout(() => {
      resetWidget()
    }, 500)
  }

  /**
   * Reset the widget
   */
  const resetWidget = () => {
    setStep('rating')
    reset()
  }

  return (
    <>
      {showTrigger && !localHideTrigger && (
        <div data-widget-open={widgetOpen} data-finished={widgetFinalized} className={classNames(styles['button-wrapper'], className)}>
          <Button
            type="button"
            variant="layered"
            onClick={() => {
              if (!widgetOpen) {
                resetWidget()
              }
              setWidgetOpen(!widgetOpen)
              trackEvent(
                { category: 'feedback', action: 'start' },
                {
                  widget: id
                }
              )
            }}
          >
            {t(($) => $.button)}
          </Button>
          <Button
            type="button"
            variant="layered"
            leadingIcon="cross"
            title={t(($) => $.hide)}
            onClick={() => {
              setLocalHideTrigger(true)
              trackEvent({ category: 'feedback', action: 'hide' }, { widget: id })
            }}
          />
        </div>
      )}

      <dialog className={styles['feedback-widget']} open={widgetOpen}>
        <Button
          size="small"
          variant="tertiary"
          leadingIcon="cross"
          onClick={() => (widgetFinalized ? closeFinalizedWidget() : closeWidget())}
          className={styles.close}
        />

        <header className={styles.header}>
          {step === 'feedback' && (
            <Button
              size="small"
              variant="tertiary"
              leadingIcon="arrowLeft"
              onClick={() => setStep('rating')}
              className={styles.back}
              title={t(($) => $.back, {
                ns: 'common'
              })}
            />
          )}

          {widgetFinalized ? (
            <div className={styles['check-wrapper']}>
              <Icon name="check" />
            </div>
          ) : (
            <Heading as="h2" variant="h5">
              {parse(currentTitle)}
            </Heading>
          )}
        </header>

        {!widgetFinalized && step !== 'userTesting' && (
          <Form action="#" onSubmit={handleSubmit(submit)} className={styles.form}>
            {/* FIRST STEP - rating */}
            {step === 'rating' && (
              <>
                <Controller
                  {...{ control }}
                  name="rating"
                  rules={{
                    required: t(($) => $.required, {
                      ns: 'validation'
                    })
                  }}
                  render={({ field: { onChange, ...register }, fieldState }) => (
                    <Rating
                      {...register}
                      limit={5}
                      hoverPreview
                      onClick={onChange}
                      label={ratingTitle}
                      minScoreLabel={t(($) => $.rating.indication.bad)}
                      maxScoreLabel={t(($) => $.rating.indication.good)}
                      error={fieldState.error?.message}
                      value={watchRating}
                      hiddenLabel
                    />
                  )}
                />

                <Form.Footer>
                  <Button type="button" isFullwidth disabled={!watchRating} onClick={() => setStep('feedback')}>
                    {t(($) => $.feedback.next)}
                  </Button>
                </Form.Footer>

                <Card as="section" color="neutral" className={styles.banner}>
                  <Heading as="h3" variant="h6">
                    {t(($) => $.rating.banner.title)}
                  </Heading>
                  <p>
                    <Trans
                      {...{ t }}
                      i18nKey={($) => $.rating.banner.description}
                      components={{
                        tel: <Link href={`tel:${BOLT_PHONE_CONTACT}`} as="a" variant="inline" />,
                        mail: <Link href={`mailto:${BOLT_EMAIL_CONTACT[language]}`} as="a" variant="inline" />
                      }}
                    />
                  </p>
                </Card>
              </>
            )}

            {/* SECOND STEP - feedback */}
            {step === 'feedback' && (
              <>
                <TextArea
                  {...register('feedback')}
                  placeholder={t(($) => $.feedback.optional)}
                  label={t(($) => $.feedback.title[watchRating > 3 ? 'happy' : 'sad'])}
                  grid="full"
                  hiddenLabel
                />
                <Form.Footer>
                  <Button type="submit" isFullwidth>
                    {t(($) => $.feedback.submit)}
                  </Button>
                </Form.Footer>
              </>
            )}
          </Form>
        )}

        {/* THIRD STEP - beta testing */}
        {!widgetFinalized && step === 'userTesting' && (
          <>
            <small>{t(($) => $.userTesting.description)}</small>
            <div className={styles['action-buttons']}>
              <Button variant="secondary" onClick={() => handleClickTestingButton(true)}>
                {t(($) => $.yes, {
                  ns: 'common'
                })}
              </Button>
              <Button variant="secondary" onClick={() => handleClickTestingButton(false)}>
                {t(($) => $.no, {
                  ns: 'common'
                })}
              </Button>
            </div>
          </>
        )}

        {/* FINAL STEP - finalized */}
        {widgetFinalized && (
          <section className={styles.finalized}>
            <Heading as="h2" variant="h5" className="mb-200">
              {t(($) => $.thank.title)}
            </Heading>
            <p>{t(($) => $.thank.description)}</p>
          </section>
        )}
      </dialog>
    </>
  )
}

export default FeedbackWidget
